
// VideoOverlayControls.tsx
// (Full file — keeps your buttons the same size, fixes PLAY vs PAUSE width, and avoids state flapping.)
import * as React from "react";
import { Box, Button, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import Replay10Icon from "@mui/icons-material/Replay10";
import Forward10Icon from "@mui/icons-material/Forward10";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

export type VideoPlaybackRate = 0.1 | 0.25 | 0.5 | 1;

let nextKeyboardOwnerId = 1;
const keyboardOwnerStack: number[] = [];

function removeKeyboardOwner(id: number) {
  for (let i = keyboardOwnerStack.length - 1; i >= 0; i -= 1) {
    if (keyboardOwnerStack[i] === id) keyboardOwnerStack.splice(i, 1);
  }
}

function promoteKeyboardOwner(id: number) {
  removeKeyboardOwner(id);
  keyboardOwnerStack.push(id);
}

function isTopKeyboardOwner(id: number) {
  return keyboardOwnerStack[keyboardOwnerStack.length - 1] === id;
}

function isEditableKeyboardTarget(target: EventTarget | null) {
  const element = target as HTMLElement | null;
  const tagName = element?.tagName?.toLowerCase();
  return (
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select" ||
    element?.isContentEditable ||
    element?.getAttribute?.("role") === "textbox" ||
    !!element?.closest?.('[contenteditable="true"]')
  );
}

function isSpaceKey(event: KeyboardEvent) {
  return event.code === "Space" || event.key === " " || event.key === "Spacebar";
}

function consumeKeyboardEvent(event: KeyboardEvent) {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
}

export function VideoOverlayControls(props: {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  rate: VideoPlaybackRate;
  setRate: (r: VideoPlaybackRate) => void;
  paused: boolean;
  setPaused: (p: boolean) => void;
  onStepFrame?: (dir: -1 | 1) => void;
  onStepShot?: (dir: -1 | 1) => void;
  onStepRally?: (dir: -1 | 1) => void;
  keyboardEnabled?: boolean;
  showUltraSlowRate?: boolean;
  placement: "top" | "bottom" | "left"; // | "right";
}) {
  const {
    videoRef,
    rate,
    setRate,
    paused,
    setPaused,
    onStepFrame,
    onStepShot,
    onStepRally,
    keyboardEnabled = true,
    showUltraSlowRate = false,
    placement,
  } = props;
  const keyboardOwnerIdRef = React.useRef<number | null>(null);
  if (keyboardOwnerIdRef.current == null) {
    keyboardOwnerIdRef.current = nextKeyboardOwnerId++;
  }
  const pausedRef = React.useRef(paused);
  const onStepFrameRef = React.useRef(onStepFrame);
  const onStepShotRef = React.useRef(onStepShot);
  const onStepRallyRef = React.useRef(onStepRally);
  const setPausedRef = React.useRef(setPaused);

  React.useEffect(() => {
    pausedRef.current = paused;
    onStepFrameRef.current = onStepFrame;
    onStepShotRef.current = onStepShot;
    onStepRallyRef.current = onStepRally;
    setPausedRef.current = setPaused;
  }, [onStepFrame, onStepRally, onStepShot, paused, setPaused]);

  const jump = React.useCallback(
    (deltaSec: number) => {
      const v = videoRef.current;
      if (!v) return;
      try {
        const t = Math.max(0, (v.currentTime ?? 0) + deltaSec);
        v.currentTime = t;
      } catch {}
    },
    [videoRef],
  );

  const setPausedFromControl = React.useCallback(
    (nextPaused: boolean) => {
      pausedRef.current = nextPaused;
      setPausedRef.current(nextPaused);

      const v = videoRef.current;
      if (!v) return;
      try {
        if (nextPaused) {
          v.pause();
        } else {
          v.muted = true;
          v.defaultMuted = true;
          v.volume = 0;
          v.play().catch(() => {});
        }
      } catch {}
    },
    [videoRef],
  );

  const togglePausedFromControl = React.useCallback(() => {
    setPausedFromControl(!pausedRef.current);
  }, [setPausedFromControl]);

  React.useEffect(() => {
    if (!keyboardEnabled) return;
    const currentKeyboardOwnerId = keyboardOwnerIdRef.current;
    if (currentKeyboardOwnerId == null) return;
    const keyboardOwnerId: number = currentKeyboardOwnerId;
    promoteKeyboardOwner(keyboardOwnerId);

    function onKeyDown(event: KeyboardEvent) {
      if (!isTopKeyboardOwner(keyboardOwnerId)) return;
      if (isEditableKeyboardTarget(event.target)) {
        return;
      }

      if (
        event.key === "ArrowLeft" &&
        event.shiftKey &&
        event.altKey &&
        onStepRallyRef.current
      ) {
        consumeKeyboardEvent(event);
        onStepRallyRef.current(-1);
      } else if (
        event.key === "ArrowRight" &&
        event.shiftKey &&
        event.altKey &&
        onStepRallyRef.current
      ) {
        consumeKeyboardEvent(event);
        onStepRallyRef.current(1);
      } else if (event.key === "ArrowLeft" && event.shiftKey && onStepShotRef.current) {
        consumeKeyboardEvent(event);
        onStepShotRef.current(-1);
      } else if (event.key === "ArrowRight" && event.shiftKey && onStepShotRef.current) {
        consumeKeyboardEvent(event);
        onStepShotRef.current(1);
      } else if (event.key === "ArrowLeft" && onStepFrameRef.current) {
        consumeKeyboardEvent(event);
        onStepFrameRef.current(-1);
      } else if (event.key === "ArrowRight" && onStepFrameRef.current) {
        consumeKeyboardEvent(event);
        onStepFrameRef.current(1);
      } else if (isSpaceKey(event)) {
        consumeKeyboardEvent(event);
        if (event.repeat) return;
        togglePausedFromControl();
      }
    }

    function onKeyUp(event: KeyboardEvent) {
      if (!isTopKeyboardOwner(keyboardOwnerId)) return;
      if (isEditableKeyboardTarget(event.target)) return;
      if (isSpaceKey(event)) {
        consumeKeyboardEvent(event);
      }
    }

    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("keyup", onKeyUp, true);
    return () => {
      removeKeyboardOwner(keyboardOwnerId);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("keyup", onKeyUp, true);
    };
  }, [keyboardEnabled, togglePausedFromControl]);
  
  const isLeft = placement === "left"; // || placement === "right";
  const tooltipPlacement = isLeft ? "right" : "top";
  const tooltipSlotProps = {
    popper: {
      sx: {
        zIndex: 7000,
      },
    },
    tooltip: {
      sx: {
        fontSize: 15,
        lineHeight: 1.25,
        px: 1.25,
        py: 0.85,
      },
    },
  };

  return (
    <Box
      sx={{
        position: "absolute",
        // left: 10,
        right: 10,
        // top: placement === "top" ? 10 : undefined,
        // bottom: placement === "bottom" ? 10 : undefined,
        ...(isLeft
        ? {
            left: 10,
            top: 10,
            bottom: 10,
            width: 54, // slim rail
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            gap: 0.8,
          }
        : placement === "top"
          ? { left: 10, right: 10, top: 10 }
          : { left: 10, right: 10, bottom: 10 }),
        zIndex: 20,
        pointerEvents: "none", // make the whole bar non-interactive except the controls themselves
        display: "flex",
        justifyContent: placement==="bottom"? "left" : "center",
      }}
    >
      <Box
        sx={{
          pointerEvents: "auto",
          display: "flex",
          alignItems: "center",
          // gap: 1,
          px: 1,
          py: 0.8,
          borderRadius: 999,
          "& button": {backgroundColor: "rgba(0,0,0,0.8) !important"},
          // bgcolor: "rgba(0,0,0,0.34)",
          // border: "1px solid rgba(255,255,255,0.16)",
          // backdropFilter: "blur(8px)",
          ...(isLeft
          ? { flexDirection: "column", gap: 0.7 }
          : { flexDirection: "row", gap: 0.9 }),
        }}
      >
        {/* <Button
          size="small"
          onClick={() => jump(-1)}
          startIcon={<Replay10Icon />}
          sx={{
            minWidth: 82,
            color: "white",
            borderRadius: 999,
            bgcolor: "rgba(255,255,255,0.10)",
            border: "1px solid rgba(255,255,255,0.10)",
            textTransform: "none",
          }}
        >
          -1s
        </Button> */}

        <Tooltip
          title="Play/pause: Space"
          placement={tooltipPlacement}
          slotProps={tooltipSlotProps}
          componentsProps={tooltipSlotProps}
        >
          <Button
            size="small"
            onClick={togglePausedFromControl}
            startIcon={paused ? <PlayArrowIcon /> : <PauseIcon />}
            sx={{
              minWidth: 40, // ✅ fixed width so PLAY/PAUSE don't resize
              color: "white",
              borderRadius: 999,
              bgcolor: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.6)",
              textTransform: "none",
              fontWeight: 600,
              letterSpacing: 0.3,
              fontSize:10,
              marginLeft: isLeft?"10px": 0,
            }}
          >
            {/* ✅ add a space so “PLAY ” is not narrower than “PAUSE” in some fonts */}
            <Box
              component="span"
              sx={{
                display: "inline-block",
                minWidth: 44, // tune until PLAY/PAUSE never shift
                textAlign: "left",
              }}
            >
              {paused ? "PLAY " : "PAUSE"}
            </Box>
          </Button>
        </Tooltip>

        <Tooltip
          title="Previous frame: ←"
          placement={tooltipPlacement}
          slotProps={tooltipSlotProps}
          componentsProps={tooltipSlotProps}
        >
          <Button
            size="small"
            onClick={() => onStepFrame?.(-1)}
            sx={{
              minWidth: isLeft ? 40 : 46,
              color: "white",
              borderRadius: 999,
              bgcolor: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.42)",
              textTransform: "none",
              fontWeight: 800,
              fontSize: 10,
              lineHeight: 1,
              px: 1,
            }}
          >
            ←1 frame
          </Button>
        </Tooltip>

        <Tooltip
          title="Next frame: →"
          placement={tooltipPlacement}
          slotProps={tooltipSlotProps}
          componentsProps={tooltipSlotProps}
        >
          <Button
            size="small"
            onClick={() => onStepFrame?.(1)}
            sx={{
              minWidth: isLeft ? 40 : 46,
              color: "white",
              borderRadius: 999,
              bgcolor: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.42)",
              textTransform: "none",
              fontWeight: 800,
              fontSize: 10,
              lineHeight: 1,
              px: 1,
            }}
          >
            1 frame→
          </Button>
        </Tooltip>

        {onStepShot ? (
          <>
            <Tooltip
              title="Previous shot: Shift+←"
              placement={tooltipPlacement}
              slotProps={tooltipSlotProps}
              componentsProps={tooltipSlotProps}
            >
              <Button
                size="small"
                onClick={() => onStepShot(-1)}
                sx={{
                  minWidth: isLeft ? 40 : 46,
                  color: "white",
                  borderRadius: 999,
                  bgcolor: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.42)",
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: 10,
                  lineHeight: 1,
                  px: 1,
                }}
              >
                ←1 shot
              </Button>
            </Tooltip>
            <Tooltip
              title="Next shot: Shift+→"
              placement={tooltipPlacement}
              slotProps={tooltipSlotProps}
              componentsProps={tooltipSlotProps}
            >
              <Button
                size="small"
                onClick={() => onStepShot(1)}
                sx={{
                  minWidth: isLeft ? 40 : 46,
                  color: "white",
                  borderRadius: 999,
                  bgcolor: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.42)",
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: 10,
                  lineHeight: 1,
                  px: 1,
                }}
              >
                1 shot→
              </Button>
            </Tooltip>
          </>
        ) : null}

        {onStepRally ? (
          <>
            <Tooltip
              title="Previous rally: Option+Shift+←"
              placement={tooltipPlacement}
              slotProps={tooltipSlotProps}
              componentsProps={tooltipSlotProps}
            >
              <Button
                size="small"
                onClick={() => onStepRally(-1)}
                sx={{
                  minWidth: isLeft ? 40 : 46,
                  color: "white",
                  borderRadius: 999,
                  bgcolor: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.42)",
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: 10,
                  lineHeight: 1,
                  px: 1,
                }}
              >
                ←1 rally
              </Button>
            </Tooltip>
            <Tooltip
              title="Next rally: Option+Shift+→"
              placement={tooltipPlacement}
              slotProps={tooltipSlotProps}
              componentsProps={tooltipSlotProps}
            >
              <Button
                size="small"
                onClick={() => onStepRally(1)}
                sx={{
                  minWidth: isLeft ? 40 : 46,
                  color: "white",
                  borderRadius: 999,
                  bgcolor: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.42)",
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: 10,
                  lineHeight: 1,
                  px: 1,
                }}
              >
                1 rally→
              </Button>
            </Tooltip>
          </>
        ) : null}

        {/* <Button
          size="small"
          onClick={() => jump(+1)}
          startIcon={<Forward10Icon />}
          sx={{
            minWidth: 82,
            color: "white",
            borderRadius: 999,
            bgcolor: "rgba(255,255,255,0.10)",
            border: "1px solid rgba(255,255,255,0.10)",
            textTransform: "none",
          }}
        >
          +1s
        </Button> */}

        <ToggleButtonGroup
          exclusive
          value={rate}
          onChange={(_, v) => {
            if (!v) return;
            setRate(v);
          }}
          size="small"
          orientation={isLeft ? "vertical" : "horizontal"}
          sx={{
            ml: 0.5,
            "& .MuiToggleButton-root": {
              color: "white",
              borderColor: "rgba(255,255,255,0.14)",
              border: "3px solid transparent !important",
              textTransform: "none",
              borderRadius: 999,
              px: 1.2,
              py: 0.3,
            },
            "& .Mui-selected": {
              color: "white !important",
              bgcolor: "rgba(0,0,0,1) !important",
              borderColor: "rgba(255,255,255,0.28) !important",
              border: "3px solid rgba(255,255,255,0.8) !important",
            },
          }}
        >
          {showUltraSlowRate ? (
            <ToggleButton value={0.1} data-demo="video-rate-0.1">
              0.1×
            </ToggleButton>
          ) : null}
          <ToggleButton value={0.25} data-demo="video-rate-0.25">
            0.25×
          </ToggleButton>
          <ToggleButton value={0.5} data-demo="video-rate-0.5">
            0.5×
          </ToggleButton>
          <ToggleButton value={1} data-demo="video-rate-1">
            1×
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
}

// // VideoOverlayControls.tsx
// import * as React from "react";
// import { Box, Button } from "@mui/material";

// import Replay10Icon from "@mui/icons-material/Replay10";
// import Forward10Icon from "@mui/icons-material/Forward10";
// import PauseIcon from "@mui/icons-material/Pause";
// import PlayArrowIcon from "@mui/icons-material/PlayArrow";

// export function seekBySeconds(video: HTMLVideoElement, deltaSeconds: number) {
//   const dur = Number.isFinite(video.duration) ? video.duration : null;
//   const next = video.currentTime + deltaSeconds;
//   if (dur == null) {
//     video.currentTime = Math.max(0, next);
//     return;
//   }
//   video.currentTime = Math.max(0, Math.min(dur, next));
// }

// export function VideoOverlayControls(props: {
//   videoRef: React.RefObject<HTMLVideoElement | null>;
//   rate: 0.25 | 0.5 | 1;
//   setRate: (r: 0.25 | 0.5 | 1) => void;
//   paused: boolean;
//   setPaused: (p: boolean) => void;
//   placement: "top" | "bottom"; // NEW
// }) {
//   const { videoRef, rate, setRate, paused, setPaused, placement } = props;

//   const applyRate = React.useCallback(
//     (r: 0.25 | 0.5 | 1) => {
//       const v = videoRef.current;
//       if (!v) return;
//       v.defaultPlaybackRate = r;
//       v.playbackRate = r;
//     },
//     [videoRef],
//   );

//   React.useEffect(() => {
//     applyRate(rate);
//   }, [rate, applyRate]);

//   const togglePlay = React.useCallback(() => {
//     const v = videoRef.current;
//     if (!v) return;

//     if (v.paused) {
//       v.play().catch(() => {});
//       setPaused(false);
//     } else {
//       v.pause();
//       setPaused(true);
//     }
//   }, [videoRef, setPaused]);

//   const step = React.useCallback(
//     (deltaSeconds: number) => {
//       const v = videoRef.current;
//       if (!v) return;
//       v.pause();
//       setPaused(true);
//       seekBySeconds(v, deltaSeconds);
//     },
//     [videoRef, setPaused],
//   );

//   const RateButton = (r: 0.25 | 0.5 | 1) => (
//     <Button
//       key={r}
//       size="small"
//       variant={rate === r ? "contained" : "outlined"}
//       onClick={() => {
//         setRate(r);
//         applyRate(r);
//       }}
//       sx={{
//         textTransform: "none",
//         minHeight: 22,
//         py: 0,
//         px: 1,
//         fontWeight: 900,
//         fontSize: 12,
//         borderRadius: 999,
//         boxShadow: rate === r ? "0 0 0 1px rgba(255,255,255,0.35)" : "none",
//         bgcolor: rate === r ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.55)",
//         color: rate === r ? "rgba(0,0,0,0.92)" : "rgba(255,255,255,0.92)",
//         borderColor: rate === r ? "rgba(0,0,0,0.18)" : "rgba(255,255,255,0.28)",
//         "&:hover": {
//           bgcolor: rate === r ? "rgba(255,255,255,0.98)" : "rgba(0,0,0,0.70)",
//         },
//       }}
//     >
//       {r}×
//     </Button>
//   );

//   return (
//     <Box
//       sx={{
//         position: "absolute",
//         right: 10,
//         ...(placement === "top"
//           ? { top: 10, bottom: "auto" }
//           : { bottom: 10, top: "auto" }),
//         display: "flex",
//         alignItems: "center",
//         gap: 0.75,
//         pointerEvents: "auto",
//         zIndex: 120,
//       }}
//     >
//       <Button
//         size="small"
//         onClick={() => step(-1)}
//         sx={{
//           minHeight: 22,
//           py: 0,
//           px: 1,
//           fontWeight: 900,
//           fontSize: 12,
//           borderRadius: 999,
//           bgcolor: "rgba(0,0,0,0.55)",
//           color: "rgba(255,255,255,0.92)",
//           border: "1px solid rgba(255,255,255,0.20)",
//           "&:hover": { bgcolor: "rgba(0,0,0,0.70)" },
//         }}
//         startIcon={<Replay10Icon sx={{ fontSize: 18 }} />}
//       >
//         -1s
//       </Button>

//       <Button
//         size="small"
//         onClick={togglePlay}
//         sx={{
//           minHeight: 22,
//           py: 0,
//           px: 1,
//           fontWeight: 900,
//           fontSize: 12,
//           borderRadius: 999,
//           bgcolor: paused ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.55)",
//           color: paused ? "rgba(0,0,0,0.92)" : "rgba(255,255,255,0.92)",
//           border: paused
//             ? "1px solid rgba(0,0,0,0.18)"
//             : "1px solid rgba(255,255,255,0.20)",
//           "&:hover": {
//             bgcolor: paused ? "rgba(255,255,255,0.98)" : "rgba(0,0,0,0.70)",
//           },
//         }}
//         startIcon={
//           paused ? (
//             <PlayArrowIcon sx={{ fontSize: 18 }} />
//           ) : (
//             <PauseIcon sx={{ fontSize: 18 }} />
//           )
//         }
//       >
//         {paused ? "Play" : "Pause"}
//       </Button>

//       <Button
//         size="small"
//         onClick={() => step(1)}
//         sx={{
//           minHeight: 22,
//           py: 0,
//           px: 1,
//           fontWeight: 900,
//           fontSize: 12,
//           borderRadius: 999,
//           bgcolor: "rgba(0,0,0,0.55)",
//           color: "rgba(255,255,255,0.92)",
//           border: "1px solid rgba(255,255,255,0.20)",
//           "&:hover": { bgcolor: "rgba(0,0,0,0.70)" },
//         }}
//         endIcon={<Forward10Icon sx={{ fontSize: 18 }} />}
//       >
//         +1s
//       </Button>

//       <Box sx={{ display: "flex", gap: 0.6, ml: 0.5 }}>
//         {RateButton(0.25)}
//         {RateButton(0.5)}
//         {RateButton(1)}
//       </Box>
//     </Box>
//   );
// }
